<template>
  <div class="mirror-form">
    <drawer-dialog-controls
      :is-loading="isSaving"
      :is-disabled="readonly"
      @close="$emit('close')"
      @save="onSave"
    >
      {{ formTitle }}
    </drawer-dialog-controls>

    <v-card-text>
      <select-project
        v-model="selectedProject"
        readonly
        :disabled="readonly"
        style="min-width: 250px; max-width: 250px"
      />

      <v-card class="mt-4">
        <v-expansion-panels v-model="mainSettingsPanel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="mirror-form__expansion-header px-4">
              <div class="d-flex align-center">
                <span class="body-1">Main settings</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="mirror-form__expansion-content">
              <v-divider />
              <div class="ma-4">
                <v-row>
                  <v-col class="py-0" cols="12" md="3">
                    <v-text-field v-model="form.name" :readonly="readonly" label="Mirror domain" />
                  </v-col>
                  <v-col class="py-0" cols="12" md="3">
                    <v-text-field v-model="form.settings.send_email" :readonly="readonly" label="Send e-mail status notifications" />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="py-0" cols="12" md="3">
                    <v-text-field v-model="form.settings.login" :readonly="readonly" label="Login" />
                  </v-col>
                  <v-col class="py-0" cols="12" md="3">
                    <v-text-field v-model="form.settings.password" :readonly="readonly" label="Password" />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center py-0">
                    <v-switch
                      v-model="form.settings.password_on"
                      :disabled="readonly"
                      label="Password ON"
                    />
                  </v-col>
                </v-row>

                <v-row class="align-center">
                  <v-col class="py-0" cols="12" md="3">
                    <v-select
                      v-model="form.group_mirror_id"
                      :disabled="readonly"
                      :items="groupItems"
                      label="Select Group"
                      item-text="text"
                      item-value="value"
                    />
                  </v-col>
                  <v-col cols="12" md="9" class="d-flex align-start justify-space-between py-0">
                    <div>
                      <div class="caption grey--text text-uppercase">Primary Mirror</div>
                      <div class="d-flex align-center">
                        <v-icon v-if="selectedGroupPrimaryName" small color="orange">mdi-crown</v-icon>
                        <span class="caption orange--text font-weight-medium ml-1">{{ selectedGroupPrimaryName }}</span>
                      </div>
                    </div>
                    <div>
                      <div class="caption grey--text text-uppercase">Default Rule</div>
                      <span class="text-capitalize caption grey--text text--darken-2">{{ selectedGroupGeo.rule }}</span>
                    </div>
                    <div v-if="selectedGroupGeo.allowedDisplay.length" class="d-flex flex-column align-center">
                      <div class="caption grey--text text-uppercase">Allowed Geo</div>
                      <lang-row
                        :countries="selectedGroupGeo.allowedDisplay"
                        type="outline"
                        color="success"
                        :max-items="3"
                        class="auto-width ml-1"
                      />
                    </div>
                    <div v-if="selectedGroupGeo.blockedDisplay.length" class="d-flex flex-column align-center">
                      <div class="caption grey--text text-uppercase">Blocked Geo</div>
                      <lang-row
                        :countries="selectedGroupGeo.blockedDisplay"
                        type="outline"
                        color="error"
                        :max-items="3"
                        class="auto-width ml-1"
                      />
                    </div>
                    <div>
                      <div class="caption grey--text text-uppercase">Redirector</div>
                      <div class="d-flex align-center">
                        <lang-row
                          :countries="selectedGroupGeo.redirect"
                          type="outline"
                          color="grey"
                          :max-items="3"
                          class="auto-width ml-1"
                        />
                        <v-icon small :color="selectedGroupRedirectorName ? 'blue' : 'grey'" class="ml-1">mdi-redo-variant</v-icon>
                          <span class="caption ml-1" :class="selectedGroupRedirectorName ? 'blue--text' : 'grey--text'">
                            {{ selectedGroupRedirectorName ? selectedGroupRedirectorName : 'none' }}
                          </span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <v-card class="mt-4">
        <v-expansion-panels v-model="geoPanel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="mirror-form__expansion-header px-4">
              <div class="d-flex align-center">
                <span class="body-1">Geo Rules</span>
                <v-tooltip right>
                  <template #activator="{ on }">
                    <v-icon small class="ml-2" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>By default, the domain is available to all countries except those that are blocked</span>
                </v-tooltip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="mirror-form__expansion-content">
              <v-divider />
              <div class="ma-4 mirror-form__expansion-content-wrapper">
                <v-simple-table dense>
                  <thead>
                    <tr class="geo-rules-table__row">
                      <th style="width: 216px" class="text-left subtitle-2 black--text">Geo Rule</th>
                      <th class="text-left subtitle-2 black--text">Value</th>
                      <th class="text-left subtitle-2 black--text">Additional</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="geo-rules-table__row">
                      <td class="body-2">Default rule</td>
                      <td>
                        <v-select
                          v-model="form.settings.geo_settings.default_rule"
                          :disabled="readonly"
                          :items="defaultRuleOptions"
                          hide-details
                          class="ma-0 py-0"
                          style="max-width: 200px"
                        />
                      </td>
                      <td />
                    </tr>

                    <tr
                      v-if="isGeoRuleVisible('redirect')"
                      class="geo-rules-table__row"
                      @click="!readonly && toggleGeoEditor('redirect')"
                    >
                      <td class="body-2">Redirect</td>
                      <td>
                        <lang-row
                          :countries="redirectRulesState"
                          type="outline"
                          color="grey"
                          :max-items="3"
                          class="auto-width ml-1"
                        />
                      </td>
                      <td class="body-2">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon small :color="selectedGroupRedirectorName ? 'blue' : 'grey'">mdi-redo-variant</v-icon>
                              <span class="ml-1 caption" :class="selectedGroupRedirectorName ? 'blue--text' : 'grey--text'">
                                Redirect → {{ selectedGroupRedirectorName ? selectedGroupRedirectorName : 'none' }}
                              </span>
                          </div>
                          <v-btn icon x-small>
                            <v-icon small>{{ geoEditors.redirect ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="isGeoRuleVisible('redirect') && geoEditors.redirect">
                      <td colspan="3" class="py-4">
                        <div class="mb-2 font-weight-medium">Redirect GEO</div>
                        <div class="caption grey--text">Geo</div>
                        <select-countries
                          :chosenCountries="redirectRulesState"
                          :countries="allCountries"
                          :disabled="readonly"
                          :chip="true"
                          chip-color="#2196F3"
                          :outlined="false"
                          @input="(items) => redirectRulesState = items.map(({ value }) => value)"
                          class="select-countries"
                        />
                      </td>
                    </tr>

                    <tr
                      v-if="isGeoRuleVisible('blocked')"
                      class="geo-rules-table__row"
                      @click="!readonly && toggleGeoEditor('blocked')"
                    >
                      <td class="body-2">Blocked Geo</td>
                      <td>
                        <lang-row
                          :countries="getGeo(form).blockedDisplay"
                          type="outline"
                          color="error"
                          :max-items="3"
                          class="auto-width ml-1"
                        />
                      </td>
                      <td>
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon small color="error">mdi-close-circle-outline</v-icon>
                            <span class="ml-1 caption">Deny (403)</span>
                          </div>
                          <v-btn icon x-small>
                            <v-icon small>{{ geoEditors.blocked ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="isGeoRuleVisible('blocked') && geoEditors.blocked">
                      <td colspan="3" class="py-4">
                        <geo-rules-editor
                          :rules="blockedRulesState"
                          :readonly="readonly"
                          @add="!readonly && addGeoRule('blocked')"
                          @remove="!readonly && removeGeoRule('blocked', $event)"
                          @update:geo="!readonly && updateGeoRuleField('blocked', $event.index, 'geo', $event.value)"
                          @update:devices="!readonly && updateGeoRuleField('blocked', $event.index, 'devices', $event.value)"
                        />
                      </td>
                    </tr>

                    <tr v-if="isGeoRuleVisible('allowed')" class="geo-rules-table__row">
                      <td class="body-2">Allowed Geo</td>
                      <td>
                        <lang-row
                          :countries="getGeo(form).allowedDisplay"
                          type="outline"
                          color="success"
                          :max-items="3"
                          class="auto-width ml-1"
                        />
                      </td>
                      <td class="py-2 body-2">
                        <div class="d-flex justify-end">
                          <v-btn v-show="!readonly" icon x-small :disabled="readonly" @click="toggleGeoEditor('allowed')">
                            <v-icon small>{{ geoEditors.allowed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="isGeoRuleVisible('allowed') && geoEditors.allowed">
                      <td colspan="3" class="py-4">
                        <geo-rules-editor
                          :rules="allowedRulesState"
                          :readonly="readonly"
                          @add="!readonly && addGeoRule('allowed')"
                          @remove="!readonly && removeGeoRule('allowed', $event)"
                          @update:geo="!readonly && updateGeoRuleField('allowed', $event.index, 'geo', $event.value)"
                          @update:devices="!readonly && updateGeoRuleField('allowed', $event.index, 'devices', $event.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <v-card class="mt-4" outlined>
        <v-expansion-panels v-model="nsPanel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="mirror-form__expansion-header px-4">
              <div class="d-flex align-center">
                <span class="body-1 black--text">Mirrors Name Servers</span>
                <v-tooltip right>
                  <template #activator="{ on }">
                    <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
                  </template>
                  Name servers assigned to this mirror
                </v-tooltip>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="mirror-form__expansion-content">
              <v-divider />
              <template v-if="nsList.length">
                <div
                  v-for="(ns, index) in nsList"
                  :key="index"
                  class="ml-4"
                >
                  <div class="d-flex align-center py-3">
                    <span class="body-1 font-weight-medium">NS{{ ns.settings?.priority ?? index + 1 }}:&nbsp;</span>
                    <span>{{ String(ns.name || '').toUpperCase() }}</span>
                    <v-btn
                      icon
                      x-small
                      class="ml-2"
                      @click="copyToClipboard(ns.name)"
                    >
                      <v-icon small color="success">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <v-divider v-if="index < nsList.length - 1" />
                </div>
              </template>
              <div v-else class="mirror-form__empty-ns">
                <div class="pt-4 grey--text subtitle-1">
                  Loading NS from Cloudflare in progress
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>



      <v-card class="mt-4" outlined>
        <v-expansion-panels v-model="featureConfigPanel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="mirror-form__expansion-header px-4">
              <div class="d-flex align-center">
                <span class="body-1">Feature Configuration</span>
                <v-tooltip right>
                  <template #activator="{ on }">
                    <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>Mirror feature config settings</span>
                </v-tooltip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="mirror-form__expansion-content">
              <v-divider />
              <div class="pa-4">
                <div class="d-flex align-center flex-wrap" style="gap: 8px">
                  <template v-if="featureTags.length > 0">
                    <span class="body-1">Custom settings:</span>
                    <v-chip
                      v-for="tag in featureTags"
                      :key="tag"
                      outlined
                      color="warning"
                      class="text-capitalize mirror-form__pill-chip"
                    >
                      {{ tagLabel(tag) }}
                    </v-chip>
                    <v-btn
                      v-if="canReadFeatureConfig && isExistingMirrorMode && mirror?.mirror_id != null"
                      icon
                      small
                      color="success"
                      @click.stop="onCustomizeMirror"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>
                    <span class="body-1">Mirror use Project setting</span>
                    <v-btn
                      v-if="canReadFeatureConfig && isExistingMirrorMode && mirror?.mirror_id != null"
                      outlined
                      small
                      color="success"
                      class="px-2"
                      @click="onCustomizeMirror"
                    >
                      Customize Mirror
                    </v-btn>
                    <v-btn
                      v-if="canReadFeatureConfig"
                      outlined
                      small
                      color="success"
                      class="px-2"
                      @click="onOpenProjectSettings"
                    >
                      Open Project Settings
                    </v-btn>
                  </template>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <v-card class="mt-4" outlined>
        <div class="mirror-form__target-geo-header px-4">
          <div class="d-flex align-center">
            <span class="body-1 black--text">Target Geo (Optional)</span>
            <v-tooltip right>
              <template #activator="{ on }">
                <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
              </template>
              Optional target geo value for mirror settings
            </v-tooltip>
          </div>
        </div>
        <v-divider />
        <div class="pa-4">
          <div class="caption mb-1">Target Geo</div>
          <select-countries
            :chosenCountries="form.settings.target_geo"
            :countries="allCountries"
            :multiple="false"
            :outlined="false"
            :disabled="readonly"
            @input="(items) => form.settings.target_geo = items.value"
            class="select-countries__target-geo"
          ></select-countries>
        </div>
      </v-card>

      <v-card class="mt-4" outlined>
        <v-expansion-panels v-model="localesPanel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="mirror-form__expansion-header px-4">
              <div class="d-flex align-center">
                <span class="body-1">Locales</span>
                <v-tooltip right>
                  <template #activator="{ on }">
                    <v-icon small class="ml-1" v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span>Available locales for this mirror</span>
                </v-tooltip>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="mirror-form__expansion-content">
              <v-divider />
              <div class="pa-4">
                <div class="mb-4">
                  <div class="d-flex align-center mb-2">
                    <span class="body-1">Default Locale</span>
                  </div>
                  <div v-if="defaultLocaleChip" class="d-flex align-center" style="gap: 6px">
                    <lang-chip
                      :lang="defaultLocaleChip"
                      color="#219653"
                      colorText="white--text"
                    />
                    <v-icon small color="success">mdi-check</v-icon>
                  </div>
                  <span v-else class="caption grey--text">Not set</span>
                </div>

                <v-divider class="mb-4" />

                <div>
                  <div class="d-flex align-center mb-3">
                    <span class="body-1">Allowed Locales</span>
                  </div>
                  <div class="d-flex align-center flex-wrap" style="gap: 8px; position: relative">
                    <div
                      v-for="locale in form.feature_config.settings.locales.list"
                      :key="locale"
                      @click="!readonly && setDefaultLocale(locale)"
                    >
                      <lang-chip
                        :lang="localeToChip(locale)"
                        :color="locale === form.feature_config.settings.locales.default ? '#219653' : '#6FCF97'"
                        :colorText="locale === form.feature_config.settings.locales.default ? 'white--text' : 'black--text'"
                      />
                    </div>

                    <select-lang
                      v-model="form.feature_config.settings.locales.list"
                      :disabled="readonly"
                      :isOpen="isLocaleSelectorOpen"
                      :isRounded="false"
                      :languages="$store.state.auth.languages"
                      @open="(val) => !readonly && (isLocaleSelectorOpen = val)"
                      style="z-index: 100"
                    >
                      <template #header>
                        <v-icon>mdi-plus</v-icon>
                      </template>
                    </select-lang>
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>


    </v-card-text>
  </div>
</template>

<script>
  import { cloneDeep, isEqual, merge } from 'lodash'
  import { DrawerDialogControls } from '@/components/core/DrawerDialog'
  import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
  import SelectLang from '@/components/core/SelectLang/SelectLang.vue'
  import LangChip from '@/components/core/SelectLang/LangChip.vue'
  import LangRow from '@/components/core/SelectLang/LangRow/LangRow.vue'
  import SelectCountries from '@/components/core/SelectCountries.vue'
  import { allCountries, mapperIsoLangToOurLocal, localeToIsoPrefix } from '@/components/core/SelectLang/mapperIsoLocale'
  import { DEFAULT_GEO_RULE_OPTIONS } from '@/modules/mirrors-redirectors/constants/const'
  import { TAG_LABELS } from '@/modules/feature-config/constants/const'
  import GeoRulesEditor from '../MirrorsGroups/GeoRulesEditor.vue'
  import mirrorMixin from '@/modules/mirrors-redirectors/mixins/mirror'

  const SKELETON_MIRROR = {
    entity_id: null,
    status: 'new',
    name: '',
    group_mirror_id: null,
    mirror_id: null,
    settings: {
      send_email: '',
      comment: '',
      login: '',
      password: '',
      password_on: false,
      target_geo: '',
      geo_settings: {
        default_rule: 'allow',
        blocked_geo: [],
        allowed_geo: [],
        redirect: [],
      },
    },
    feature_config: {
      settings_id: null,
      entity_id: null,
      mirror_id: null,

      settings: {
        locales: {
          list: [],
          default: null,
        },
      },
    },
  }

  const GEO_RULES_MAP = {
    blocked: { stateKey: 'blockedRulesState' },
    allowed: { stateKey: 'allowedRulesState' },
  }

  export default {
    name: 'MirrorForm',
    mixins: [mirrorMixin],
    components: { DrawerDialogControls, SelectProject, SelectLang, LangChip, LangRow, SelectCountries, GeoRulesEditor },
    props: {
      mirror: { type: Object, default: null },
      mirrors: { type: Array, default: () => [] },
      groups: { type: Array, default: () => [] },
      redirectors: { type: Array, default: () => [] },
      project: { type: Number, default: null },
      project_config: { type: Object, default: null },
      readonly: { type: Boolean, default: false },
      isDuplicate: { type: Boolean, default: false },
      isSaving: { type: Boolean, default: false },
      canReadFeatureConfig: { type: Boolean, default: false },
    },
    data() {
      return {
        form: cloneDeep(SKELETON_MIRROR),
        blockedRulesState: [],
        allowedRulesState: [],
        redirectRulesState: [],
        selectedProject: null,
        isLocaleSelectorOpen: false,
        allCountries,
        mainSettingsPanel: 0,
        geoPanel: 0,
        nsPanel: 0,
        localesPanel: 0,
        featureConfigPanel: 0,
        geoEditors: { redirect: false, blocked: false, allowed: false },
        defaultRuleOptions: DEFAULT_GEO_RULE_OPTIONS,
      }
    },
    computed: {
      defaultRule() {
        return this.form.settings.geo_settings.default_rule
      },
      formTitle() {
        if (this.readonly) return 'Overview Mirror'
        if (this.isDuplicate) return 'Duplicate Mirror'
        return this.mirror ? 'Edit Mirror' : 'Add New Mirror'
      },
      defaultLocaleChip() {
        const locale = this.form.feature_config.settings.locales.default
        return locale ? this.localeToChip(locale) : null
      },
      groupItems() {
        return this.groups?.map((g) => ({ text: g.name, value: g.group_mirror_id }))
      },
      selectedGroup() {
        return this.groups?.find((g) => g.group_mirror_id === this.form.group_mirror_id) || null
      },
      selectedGroupRedirectorName() {
        return this.redirectors.find((r) => r.group_mirror_id === this.form.group_mirror_id)?.name ?? ''
      },
      selectedGroupGeo() {
        return this.getGeo(this.selectedGroup || {})
      },
      selectedGroupPrimaryName() {
        const primaryId = this.selectedGroup?.primary_mirror_id
        if (!primaryId) return ''
        return this.mirrors.find((m) => m.mirror_id === primaryId)?.name || ''
      },
      nsList() {
        return (this.mirror?.settings?.ns?.items || []).filter((ns) => ns?.enabled !== false)
      },

      isExistingMirrorMode() {
        return this.mirror != null && !this.isDuplicate
      },
      featureTags() {
        return Array.isArray(this.mirror?.feature_config?.tags) ? this.mirror.feature_config.tags : []
      },

    },
    watch: {
      mirror: {
        immediate: true,
        deep: true,
        handler(mirror) {
          this.initFromMirror(mirror)
        },
      },
      defaultRule() {
        const keys = Object.keys(this.geoEditors)
        for (let i = 0; i < keys.length; i += 1) this.geoEditors[keys[i]] = false
      },
      'form.feature_config.settings.locales.list': {
        deep: true,
        handler(list = []) {
          const locales = this.form.feature_config.settings.locales
          if (!list.length) {
            locales.default = null
            return
          }
          if (!list.includes(locales.default)) {
            locales.default = list[0]
          }
        },
      },
    },
    methods: {
      isGeoRuleVisible(ruleType) {
        const visibilityByRule = {
          allow: { redirect: true, blocked: true, allowed: false },
          deny: { redirect: true, blocked: false, allowed: true },
          redir: { redirect: false, blocked: true, allowed: true },
        }
        return visibilityByRule[this.defaultRule]?.[ruleType] ?? false
      },
      getNewSkeleton(mirror) {
        const base = cloneDeep(SKELETON_MIRROR)
        if (!mirror) return { ...base, entity_id: this.project, feature_config: { ...base.feature_config, entity_id: this.project } }
        const featureConfig = mirror?.feature_config?.config || base.feature_config

        return merge(base, cloneDeep({...mirror, feature_config: mirror?.feature_config?.config}), {
          entity_id: mirror.entity_id ?? this.project,
          group_mirror_id: mirror.group_mirror_id ?? base.group_mirror_id,
          feature_config: cloneDeep(featureConfig),
        })
      },
      resolveLocales(mirror) {
        const source = mirror?.feature_config?.config?.settings?.locales
          ?? this.project_config?.settings?.locales
          ?? {};

        return {
          list:    source.list ?? [],
          default: source.default ?? null,
        };
      },
      buildMirrorFeatureSettingsDiff(mirrorSettings = {}, projectSettings = {}) {
        const diff = {}
        Object.keys(mirrorSettings).forEach((key) => {
          if (!isEqual(mirrorSettings[key], projectSettings[key])) {
            diff[key] = cloneDeep(mirrorSettings[key])
          }
        })
        return diff
      },

      normalizeFeatureConfigForSave(featureConfig) {
        if (!featureConfig) return featureConfig
        const projectSettings = this.project_config?.settings || {}
        const mirrorSettings = featureConfig.settings || {}

        return {
          ...featureConfig,
          settings: this.buildMirrorFeatureSettingsDiff(mirrorSettings, projectSettings),
        }
      },
      initFromMirror(mirror) {
        const nextForm = this.getNewSkeleton(mirror)

        this.$set(nextForm.feature_config.settings, 'locales', this.resolveLocales(mirror))
        if (this.isDuplicate) {
          nextForm.name = ''
          nextForm.mirror_id = null
          if (nextForm.feature_config) {
            nextForm.feature_config.settings_id = null
            nextForm.feature_config.mirror_id = null
          }
        }

        this.form = nextForm
        this.selectedProject = this.project
        this.blockedRulesState = this.parseGeoList(nextForm.settings.geo_settings.blocked_geo)
        this.allowedRulesState = this.parseGeoList(nextForm.settings.geo_settings.allowed_geo)
        this.redirectRulesState = nextForm.settings.geo_settings.redirect ?? []
      },

      localeToChip(locale) {
        return {
          locale,
          urlFlag: mapperIsoLangToOurLocal(localeToIsoPrefix(locale)),
        }
      },

      tagLabel(tag) {
        return TAG_LABELS[tag] || tag
      },
      onCustomizeMirror() {
        if (!this.isExistingMirrorMode || this.mirror?.mirror_id == null) return
        this.goToFeatureConfigMirror(this.mirror.mirror_id)
      },
      onOpenProjectSettings() {
        this.goToFeatureConfig()
      },

      setDefaultLocale(locale) {
        if (this.readonly) return
        this.form.feature_config.settings.locales.default = locale
      },
      toggleGeoEditor(key) {
        if (this.readonly) return
        this.geoEditors[key] = !this.geoEditors[key]
      },
      addGeoRule(key) {
        if (this.readonly) return
        this[GEO_RULES_MAP[key].stateKey].push({ geo: null, devices: [] })
      },
      removeGeoRule(key, index) {
        if (this.readonly) return
        this[GEO_RULES_MAP[key].stateKey].splice(index, 1)
      },
      updateGeoRuleField(key, index, prop, value) {
        if (this.readonly) return
        const state = this[GEO_RULES_MAP[key].stateKey]
        if (state[index]) state[index][prop] = value
      },

      getGeo(scope) {
        return this.getGeoForEditableForm({
          scope,
          form: this.form,
          blockedRulesState: this.blockedRulesState,
          allowedRulesState: this.allowedRulesState,
          redirectRulesState: this.redirectRulesState,
        })
      },

      copyToClipboard(text) {
        if (!text) return
        navigator.clipboard.writeText(text).then(() => {
          this.$showSuccess('Copied to clipboard')
        })
      },

      async onSave() {
        if (this.readonly) return
        const payload = cloneDeep(this.form)
        payload.entity_id = this.project

        this.$set(
          payload.feature_config.settings,
          'locales',
          cloneDeep(this.form.feature_config.settings.locales),
        )
        this.$set(payload.settings, 'geo_settings', {
          ...payload.settings?.geo_settings,
          blocked_geo: this.formatGeoList(this.blockedRulesState),
          allowed_geo: this.formatGeoList(this.allowedRulesState),
          redirect: this.redirectRulesState.filter(Boolean),
        })

        delete payload.settings?.ns

        if (this.isDuplicate) {
          payload.mirror_id = null
          if (payload.feature_config) {
            payload.feature_config.settings_id = null
            payload.feature_config.mirror_id = null
          }
        }
        payload.feature_config = this.normalizeFeatureConfigForSave(payload.feature_config)

        this.$emit('save', { mirror: payload })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .mirror-form {
    &__expansion-header {
      min-height: 56px !important;
    }
    &__expansion-content {
      ::v-deep .v-expansion-panel-content__wrap {
        padding: 0;
      }
      &-wrapper {
        border: 1px solid #D9D9D9;
        border-radius: 4px;
      }
    }

    &__empty-ns {
      min-height: 128px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__target-geo-header {
      min-height: 56px;
      display: flex;
      align-items: center;
    }

    &__pill-chip {
      height: 24px;
      min-height: 24px;
      padding: 5px 8px;
      border-radius: 50px;
      font-size: 12px;
    }
  }

  .select-countries {
    ::v-deep .custom-select {
      margin-left: 0;
      max-width: 548px;
    }

    &__target-geo {
      ::v-deep .custom-select {
        margin-left: 0;
        max-width: 230px;
      }
    }
  }

  .geo-rules-table__row {
    height: 48px;
  }

  .auto-width {
    width: auto !important;
  }
</style>

